import { Box3, Group, LineBasicMaterial, LineSegments, Vector3 } from 'three';

import { GeodataBoundaryPolygonModel } from '../../../../../../../../../typings/api/skymap/rest/v1/.common';
import { WebApi } from '../../../../../../../services/web-api';
import { GltfLoader } from '../../../../../../../viewer/loader/gltf';
import { renderOrders } from '../../../../../../../viewer/render-orders';

export type LoadedBoundaryPolygon = {
  boundaryGroup: Group;
  boundingBox: Box3;
  offset: Vector3;
  success: boolean;
  error?: string;
};

/**
 * Loads a single boundary polygon GLB file and returns the processed scene with bounding box and offset
 */
export async function loadBoundaryPolygonGlb(
  polygon: GeodataBoundaryPolygonModel,
): Promise<LoadedBoundaryPolygon> {
  if (!polygon.simDataUrl) {
    return {
      boundaryGroup: new Group(),
      boundingBox: new Box3(),
      offset: new Vector3(),
      success: false,
      error: 'No simDataUrl provided',
    };
  }

  try {
    // Fetch the GLB data from the URL
    const arrayBuffer = await WebApi.fetchS3DataAsArrayBuffer(polygon.simDataUrl);

    // Parse the GLB data using GltfLoader
    const gltf = await GltfLoader.instance.loadGltf(Buffer.from(arrayBuffer));

    // Create a group for this boundary polygon
    const boundaryGroup = new Group();
    boundaryGroup.name = `boundary-polygon-${polygon.id}`;
    boundaryGroup.userData = {
      boundaryPolygonId: polygon.id,
      fileName: polygon.fileName,
      boundaryType: polygon.boundaryType,
    };

    let boundingBox = new Box3();
    let offset = new Vector3();

    // Add the GLB scene to the group
    if (gltf.scene) {
      gltf.scene.traverse((child) => {
        if (child instanceof LineSegments) {
          // blue if outer, red if inner
          // should always appear in front of other objects
          child.material = new LineBasicMaterial({
            color: polygon.boundaryType === 'Outer' ? 0x0000ff : 0xff0000,
            depthTest: false,
            depthWrite: false,
          });
          child.renderOrder = renderOrders.aligner.boundaryPolygon;
        }
      });

      gltf.scene.renderOrder = renderOrders.aligner.boundaryPolygon;
      gltf.scene.userData = {
        boundaryPolygonId: polygon.id,
        fileName: polygon.fileName,
        boundaryType: polygon.boundaryType,
      };
      boundaryGroup.add(gltf.scene);

      // Calculate the bounding box in original coordiantes.
      boundingBox = new Box3().setFromObject(gltf.scene);
      // Calculate offset as the min of the bounding box (before normalization)
      offset = boundingBox.min.clone();

      // Subtract offset from the scene position to normalize coordinates
      if (gltf.scene.children.length > 0) {
        gltf.scene.children[0].position.sub(offset);
      }
    }

    return {
      boundaryGroup,
      boundingBox,
      offset,
      success: true,
    };
  } catch (error) {
    console.error(`Failed to load GLB for boundary polygon ${polygon.id}:`, error);
    return {
      boundaryGroup: new Group(),
      boundingBox: new Box3(),
      offset: new Vector3(),
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}
